import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import gqlTagSwag from 'graphql-tag';
import { fg } from '@atlassian/jira-feature-gating';
import { PAGE_SIZE } from '../../../common/constants.tsx';
import { epicFragment, estimationInfoFragment } from '../../../common/graphql/fragments/index.tsx';
import type {
	FullEpicDetails as FullEpicDetailsResponse,
	FullEpicDetailsVariables,
} from './__generated_apollo__/FullEpicDetails.ts';
/**
 * Query to fetch full epic details for the epic panel.
 */
const EPIC_DETAILS_QUERY = gqlTagSwag`
    ${epicFragment}
    ${estimationInfoFragment}
    query FullEpicDetails($boardId: ID!, $isCompanyManaged: Boolean!, $pageSize: Int!, $startCursor: String) {
        boardScope(boardId: $boardId, isCMP: $isCompanyManaged) {
            parents(first: $pageSize, after: $startCursor) {
                pageInfo {
                    hasNextPage
                    endCursor
                }
                edges {
                    node {
                        ...EpicPanelIssueParentFragment
                        estimationInfo @include(if: $isCompanyManaged) {
                            ...EstimationInfoFragment
                        }
                    }
                }
            }
        }
    }
`;

export const useEpicDetailsQuery = ({
	boardId,
	isCompanyManaged,
}: {
	boardId: number;
	isCompanyManaged: boolean;
}) =>
	useQuery<FullEpicDetailsResponse, FullEpicDetailsVariables>(EPIC_DETAILS_QUERY, {
		notifyOnNetworkStatusChange: true,
		context: {
			headers: {
				'is-company-managed-board': isCompanyManaged,
			},
		},
		variables: {
			boardId: String(boardId),
			isCompanyManaged,
			pageSize: PAGE_SIZE,
		},
		fetchPolicy: fg('prefetch-epics-data-backlog') ? 'cache-first' : 'network-only',
		skip: true,
	});

export const useEpicDetailsQueryLazy = ({
	boardId,
	isCompanyManaged,
}: {
	boardId: number;
	isCompanyManaged: boolean;
}) =>
	useLazyQuery<FullEpicDetailsResponse, FullEpicDetailsVariables>(EPIC_DETAILS_QUERY, {
		notifyOnNetworkStatusChange: true,
		context: {
			headers: {
				'is-company-managed-board': isCompanyManaged,
			},
		},
		variables: {
			boardId: String(boardId),
			isCompanyManaged,
			pageSize: PAGE_SIZE,
		},
		fetchPolicy: 'cache-first',
	});
